import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col } from 'antd';
import { Eye, EyeOff, X, ChevronDown } from 'lucide-react';
import type { Department } from '../../types';
import styles from './PersonnelModal.module.css';

const { Option } = Select;

export interface Role {
  id: string;
  name: string;
  permissions?: string[];
}

export interface AddPersonnelFormData {
  fullName: string;
  email: string;
  department: string;
  position: string;
  password?: string;
  confirmPassword?: string;
  role: string;
}

export interface AddPersonnelModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddPersonnelFormData) => void;
  departments: Department[];
  roles?: Role[];
}

// Mock roles nếu chưa có - Map với backend UserRole enum
const DEFAULT_ROLES: Role[] = [
  { id: 'admin', name: 'Quản trị viên', permissions: ['all'] },
  { id: 'ceo', name: 'Giám đốc', permissions: ['view', 'edit', 'approve'] },
  { id: 'hr', name: 'Nhân sự', permissions: ['view', 'edit'] },
  { id: 'staff', name: 'Nhân viên', permissions: ['view'] },
];

const passwordIconRender = (visible: boolean) =>
  visible ? (
    <EyeOff size={16} className={styles.eyeIcon} />
  ) : (
    <Eye size={16} className={styles.eyeIcon} />
  );

const passwordValidator = (_: unknown, value: string) => {
  // Let the built-in `required` rule handle empty values to avoid duplicate messages
  if (!value) {
    return Promise.resolve();
  }
  if (value.length < 8) {
    return Promise.reject(new Error('Mật khẩu phải có ít nhất 8 ký tự'));
  }
  if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
    return Promise.reject(new Error('Mật khẩu phải chứa ít nhất 1 chữ hoa, 1 chữ thường và 1 số'));
  }
  return Promise.resolve();
};

export const AddPersonnelModal: React.FC<AddPersonnelModalProps> = ({
  open,
  onClose,
  onSubmit,
  departments,
  roles = DEFAULT_ROLES,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const confirmPasswordValidator = (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const password = form.getFieldValue('password');
    if (value && password && value !== password) {
      return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
    }
    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(values);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={800}
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Thêm nhân sự mới</h2>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={styles.form}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="fullName"
                label="Họ và tên *"
                rules={[
                  { required: true, message: 'Vui lòng nhập họ và tên' },
                ]}
              >
                <Input
                  placeholder="Nhập họ và tên"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="email"
                label="Email *"
                rules={[
                  { required: true, message: 'Vui lòng nhập email' },
                  { type: 'email', message: 'Email không hợp lệ' },
                ]}
              >
                <Input
                  placeholder="Email"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="department"
                label="Phòng ban *"
                rules={[
                  { required: true, message: 'Vui lòng chọn phòng ban' },
                ]}
              >
                <Select
                  placeholder="Chọn phòng ban"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.name}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="position"
                label="Vị trí *"
                rules={[
                  { required: true, message: 'Vui lòng nhập vị trí' },
                ]}
              >
                <Input
                  placeholder="Nhập vị trí"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="role"
                label="Chức vụ và phân quyền *"
                rules={[
                  { required: true, message: 'Vui lòng chọn chức vụ' },
                ]}
              >
                <Select
                  placeholder="Chọn chức vụ"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                >
                  {roles.map((role) => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Mật khẩu *"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu' },
                  { validator: passwordValidator },
                ]}
              >
                <Input.Password
                  placeholder="Nhập mật khẩu"
                  size="large"
                  className={styles.input}
                  iconRender={passwordIconRender}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Xác nhận mật khẩu *"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Vui lòng xác nhận mật khẩu' },
                  { validator: confirmPasswordValidator },
                ]}
              >
                <Input.Password
                  placeholder="Nhập lại mật khẩu"
                  size="large"
                  className={styles.input}
                  iconRender={passwordIconRender}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className={styles.modalFooter}>
            <Button
              size="large"
              onClick={handleCancel}
              className={styles.cancelButton}
            >
              Huỷ
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading}
              className={styles.submitButton}
            >
              Tạo mới
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

